import { Grid } from '@material-ui/core';
import {
  DefaultCard,
  FormHookResult,
  FormValueTypes,
  TextField,
} from 'design-system';
import React, { useMemo, useRef } from 'react';

// Functions
import isAlphaNumeric from 'src/functions/isAlphaNumeric';

// Components
import { InstructionsCard } from 'src/components/general';

export const licenseNetworkError =
  'No se pudo validar la placa, por favor intente nuevamente.';
export const vinNetworkError =
  'No se pudo verificar el vin, por favor intente nuevamente';
export const motorNetworkError =
  'No se pudo verificar el motor, por favor intente nuevamente';

type UpdateMultipleInputType = (newInputdata: {
  [key: string]: FormValueTypes;
}) => void;
type PolicySource = 'licensePlate' | 'vinNumber' | 'motorNumber' | '';

export type UseCompleteCarIdentificationFormHookProps = {
  getInputProps: FormHookResult;
  updateMultipleInputs: UpdateMultipleInputType;
  onDialogueRequested: () => void;
  minDesktopCardHeight?: number;
  hasLicense?: boolean;
  hasSerialMotor?: boolean;
};
export type UseCompleteCarIdentificationFormHookResult = {
  licensePlateInputCard: React.ReactElement;
  vinNumberInputCard: React.ReactElement;
  serialMotorInputCard: React.ReactElement;
  dataIsInvalid: boolean;
  policySource: PolicySource;
};

export const setLicensePlateValue = (
  licenseValue: string,
  type: string,
): string => {
  let newValue = licenseValue.toUpperCase();
  if (type === 'K') {
    // Verifica si el valor ya comienza con "CL"
    if (newValue.startsWith('CL')) {
      newValue = newValue.replace(/^CL/, '');
    }
    if (newValue === 'C') {
      newValue = '';
    }
  }
  return newValue;
};

export default function useCompleteCarIdentificationForm({
  getInputProps,
  minDesktopCardHeight,
  hasSerialMotor,
}: UseCompleteCarIdentificationFormHookProps): UseCompleteCarIdentificationFormHookResult {
  /** The values were are interested in */
  const licensePlate = getInputProps('licensePlate').value as string;
  const vinNumber = getInputProps('vinNumber').value as string;
  const serialMotor = getInputProps('serialMotor').value as string;
  const type = getInputProps('type').value as string;
  const initialVinNumber = useRef(!!vinNumber);
  const initialLicensePlate = useRef(!!licensePlate);

  /** Validation errors */
  const licenseValidationError = getInputProps('licensePlate').error;
  const vinNumberValidationError = getInputProps('vinNumber').error;
  const serialMotorValidationError = getInputProps('serialMotor').error;

  /** Change */
  const licenseChange = getInputProps('licensePlate').onChange;
  const vinChange = getInputProps('vinNumber').onChange;
  const motorChange = getInputProps('serialMotor').onChange;

  /**
   * This variable will be true if the "next" button needs to be disabled in the containing form
   */
  const dataIsInvalid = useMemo(
    () => licensePlate === '' || vinNumber === '',
    [licensePlate, vinNumber],
  );

  const adjustedDefaultValue =
    type === 'K' ? `CL${licensePlate}` : licensePlate;

  const handleLicenseChange = (val: string) => {
    licenseChange(setLicensePlateValue(val, type));
  };

  /**
   * A reference to the license plate field
   */
  const licensePlateInputCard = (
    <DefaultCard title="Número de Placa" forcedMinHeight={minDesktopCardHeight}>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            inputId="sales-web-inspection-input-licenseplate"
            inputName="licensePlate"
            inputLabel="Placa"
            defaultValue={adjustedDefaultValue}
            inputError={licenseValidationError}
            isDisabled={initialLicensePlate.current}
            onChange={(val: string): void => handleLicenseChange(val)}
            shouldUpdate={isAlphaNumeric}
          />
        </Grid>
      </Grid>
    </DefaultCard>
  );

  /**
   * A reference to the vin number field
   */
  const vinNumberInputCard = (
    <InstructionsCard
      title="VIN o número de Chasis"
      question="¿Dónde está mi VIN o No. de chasis?"
      answer="Lo puede encontrar en el Registro Vehicular o en estos 4 lugares en
      el Auto"
      dialogTitle="¿Dónde está mi VIN?"
      imagePath="images/vin_instructions.svg"
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            inputId="sales-web-inspection-input-vin"
            inputName="vinNumber"
            inputLabel="VIN"
            isDisabled={initialVinNumber.current}
            defaultValue={vinNumber}
            inputError={vinNumberValidationError}
            onChange={(val): void => vinChange(val.toUpperCase())}
            shouldUpdate={isAlphaNumeric}
          />
        </Grid>
      </Grid>
    </InstructionsCard>
  );

  /**
   * A reference to the license plate field
   */
  const serialMotorInputCard = (
    <InstructionsCard
      title="Número de motor"
      question="¿Dónde está el número de motor?"
      answer="Lo puedes encontrar en el Registro Vehicular o en esta parte del auto"
      dialogTitle="¿Dónde está el número de motor?"
      imagePath="images/serial_motor_instructions.svg"
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <TextField
            inputId="sales-web-inspection-input-serialmotor"
            inputName="serialMotor"
            inputLabel="Motor"
            defaultValue={serialMotor}
            inputError={serialMotorValidationError}
            isDisabled={hasSerialMotor}
            onChange={(val: string): void => motorChange(val.toUpperCase())}
            shouldUpdate={isAlphaNumeric}
          />
        </Grid>
      </Grid>
    </InstructionsCard>
  );

  return {
    licensePlateInputCard,
    vinNumberInputCard,
    dataIsInvalid,
    policySource: 'licensePlate',
    serialMotorInputCard,
  };
}
